import { Accordion, AccordionDetails, AccordionSummary, Box, Stack, Typography } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'

import Textfield from 'components/FormsUI/Textfield'
import { getAvgCheckHighThreshold, applyAvgCheckAdjustment } from 'services/thresholds/thresholdService';

const fields = ['ProjSales', 'ProjCheckCount', 'ProjAvgCheck', 'TheoFOH', 'TheoBOH', 'SchedFOH', 'SchedBOH']

export default function ProjectionRow({
  day,
  data,
  getTextFieldValue,
  handleTextFieldChange,
  curYear,
  expanded,
  onChange,
  style,
  thresholds,
  storeMap,
  storeIdMap
}){
  const panel = `${day}panel`
  
  return (
    <Box
      sx={{
        pt: 1,
      }}
      style={style}
    >
      <Accordion
        expanded={expanded}
        onChange={onChange(panel)}
      >
        <AccordionSummary
          expandIcon={
            <Box
              sx={{
                fontSize: '20px',
                pr: 1,         
              }}
            >              
              <ExpandMoreIcon />          
            </Box>
          }
          sx={{ flexDirection: 'row-reverse'}}
          aria-controls={panel}
          id={`${panel}-header`}
        >      
          <Stack
            direction='row'
            spacing={1}
            alignItems='center'
          >
            <Box
              sx={{
                writingMode: 'vertical-rl',
                textOrientation: 'upright',
                fontSize: '12px',
                textAlign: 'center',
                // borderRight: 1,
                pr: 1
              }}
            >            
              {day}           
            </Box>
            {fields.map(field => (
              <SPLHField 
                key={`${day}-${field}`}
                day={day}
                field={field}
                value={getTextFieldValue(day, field)}
                onChange={handleTextFieldChange}
                year={curYear}
              />
            ))}
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          <DetailsPanel
            year={curYear}
            day={day}
            data={data}
            thresholds={thresholds}
            storeMap={storeMap}
            storeIdMap={storeIdMap}
          />
        </AccordionDetails>
      </Accordion>
    </Box>
  )
}

function SPLHField(props){
  switch(props.field){
    case 'TheoFOH':
    case 'TheoBOH':
    case 'ProjAvgCheck':
      return (
        <Textfield 
          name={`${props.day.toLowerCase()}${props.field}`}
          label={ getFieldLabel(props.field) }
          value={ props.value}
          disabled={true}
        />
      )    
    case 'ForecastedSales':
    case 'Average':
    case 'OneYearAgoSales':
    case 'TwoYearsAgoSales':
      return (
        <Textfield 
          name={`${props.day.substring(0,3).toLowerCase()}${props.field}`}
          label={ getFieldLabel(props.field)}
          disabled={true}
        />
      )
    case 'ProjCheckCount': 
      return (
        <Textfield 
          name={`${props.day.substring(0,3).toLowerCase()}ProjCheckCount`}
          label='Projected Check Count'
          value={ props.value }
          disabled={true}
        />
      )
    default:
      return (
        <Textfield 
          name={`${props.day.substring(0,3).toLowerCase()}${props.field}`}
          label={ getFieldLabel(props.field) }
          onChange={ props.onChange }
        />
      )
  }
}

function getFieldLabel(field){
  switch(field){
    case 'ProjSales':
      return 'Projected Sales'
    case 'ProjAvgCheck':
      return 'Projected Avg Check'
    case 'TheoFOH':
      return 'FOH Theoretical Hours'
    case 'TheoBOH':
      return 'BOH Theoretical Hours'
    case 'SchedFOH':
      return 'FOH Scheduled Hours'
    case 'SchedBOH':
      return 'BOH Scheduled Hours'
    default:
      return null
  }
}

function DetailsPanel({
  year,
  day,
  data = {},
  thresholds,
  storeMap,
  storeIdMap
}){
  const key = day.toLowerCase()
  const oneYearAgoSales = data[`${key}OneYearAgoSales`] || 0
  const twoYearsAgoSales = data[`${key}TwoYearsAgoSales`] || 0
  const oneYearAgoNotes = data[`${key}OneYearAgoNotes`] || ' '
  const twoYearsAgoNotes = data[`${key}TwoYearsAgoNotes`] || ' '
  // const lastYearCheckCount = data[`${key}LastYearCheckCount`] || 0
  const rawAvgCheck = parseFloat(data[`${key}OneYearAgoAvgCheck`]) || 0.00

  // Apply threshold adjustment for display - use storeIdMap if we have storeRecordId
  const storeInfo = data.storeRecordId
    ? storeIdMap?.get(data.storeRecordId)
    : (data.storeLongName && storeMap?.get(data.storeLongName))
  const storeThresholds = storeInfo?.thresholds
  const avgCheckThresholdId = storeThresholds?.avgCheck

  const highThreshold = getAvgCheckHighThreshold(
    data.storeRecordId || data.storeId,
    thresholds,
    avgCheckThresholdId
  )

  const avgCheck = applyAvgCheckAdjustment(rawAvgCheck, highThreshold)

  const average = data[`${key}Average`] || 0
  const oneYearAgoDate = data[`${key}OneYearAgoDate`]
  const twoYearsAgoDate = data[`${key}TwoYearsAgoDate`]

  return (
    <Box>
      <Stack
        direction='row'
        spacing={2}
        justifyContent='space-between'
      >
        {/* Left side: 2024 info */}
        <Stack spacing={1} width='50%'>
          <Box>
            <Typography sx={{ fontSize: '.9rem', color: '#7030a0', fontWeight: 'bold' }}>Avg Check | {oneYearAgoDate}</Typography>
            <Box sx={{ backgroundColor: '#f2cfee', borderRadius: 1, p: 1 }}
            >
              <Typography sx={{ fontSize: '.85rem', color: '#7030a0' }} >${avgCheck.toFixed(2)}</Typography>
            </Box>
          </Box>
          <Box>
            <Typography sx={{ fontSize: '.9rem', color: '#7030a0', fontWeight: 'bold'}}>Sales | {oneYearAgoDate}</Typography>
            <Box sx={{ backgroundColor: '#f2cfee', borderRadius: 1, p: 1 }}>
              <Typography sx={{ fontSize: '.85rem', color: '#7030a0'}}>${oneYearAgoSales}</Typography>
            </Box>
          </Box>
          {/* <Box>
            <Typography sx={{ fontSize: '.9rem', color: '#7030a0', fontWeight: 'bold'}}>Notes | {oneYearAgoDate}</Typography>
            <Box sx={{ backgroundColor: '#f2cfee', borderRadius: 1, p: 1 }}>
              <Typography sx={{ fontSize: '.85rem', color: '#7030a0', }}>{oneYearAgoNotes}</Typography>
            </Box>
          </Box> */}
        </Stack>
        {/* Right side: 2-Week Avg and 2023 info */}
        <Stack spacing={1} width='50%'>
          <Box>
            <Typography sx={{ fontSize: '.9rem', color: '#275317', fontWeight: 'bold'}}>Avg {day} Sales | Last 2-Weeks</Typography>
            <Box sx={{ backgroundColor: '#d9f2d0', borderRadius: 1, p: 1 }}>
              <Typography sx={{ fontSize: '.85rem', color: '#275317' }}>${average}</Typography>
            </Box>
          </Box>
          <Box>
            <Typography sx={{ fontSize: '.9rem', color: '#0070c0', fontWeight: 'bold'}}>Sales | {twoYearsAgoDate}</Typography>
            <Box sx={{ backgroundColor: '#caeefb', borderRadius: 1, p: 1 }}>
              <Typography sx={{ fontSize: '.85rem', color: '#0070c0' }}>${twoYearsAgoSales}</Typography>
            </Box>
          </Box>
          {/* <Box>
            <Typography sx={{ fontSize: '.9rem', color: '#0070c0', fontWeight: 'bold'}}>Notes | {twoYearsAgoDate}</Typography>
            <Box sx={{ backgroundColor: '#caeefb', borderRadius: 1, p: 1 }}>
              <Typography sx={{ fontSize: '.85rem', color: '#0070c0' }}>{twoYearsAgoNotes}</Typography>
            </Box>
          </Box> */}
        </Stack>
      </Stack>
    </Box>
  )
}